import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAuthStore } from '../../store/authStore'
import { getErrorMessage } from '../../types'
import api from '../../services/api'
import toast from 'react-hot-toast'

export default function ProfileSettings() {
  const { user, setUser } = useAuthStore()
  const [email, setEmail] = useState(user?.email || '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const updateEmailMutation = useMutation({
    mutationFn: async (newEmail: string) => {
      const response = await api.patch('/auth/profile', { email: newEmail })
      return response.data
    },
    onSuccess: (data) => {
      setUser(data)
      toast.success('Email обновлён')
    },
    onError: () => {
      toast.error('Ошибка при обновлении email')
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: async (data: { current_password: string; new_password: string }) => {
      const response = await api.post('/auth/change-password', data)
      return response.data
    },
    onSuccess: () => {
      toast.success('Пароль изменён')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error))
    },
  })

  const resendVerificationMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/auth/resend-verification')
      return response.data
    },
    onSuccess: () => {
      toast.success('Письмо с подтверждением отправлено')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleUpdateEmail = (e: React.FormEvent) => {
    e.preventDefault()
    if (email !== user?.email) {
      updateEmailMutation.mutate(email)
    }
  }

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      toast.error('Пароли не совпадают')
      return
    }
    if (newPassword.length < 8) {
      toast.error('Пароль должен быть не менее 8 символов')
      return
    }
    changePasswordMutation.mutate({
      current_password: currentPassword,
      new_password: newPassword,
    })
  }

  return (
    <div className="space-y-6">
      {/* Email Settings */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Email</h2>
        <form onSubmit={handleUpdateEmail} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email адрес
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input w-full max-w-md"
            />
          </div>
          <button
            type="submit"
            disabled={email === user?.email || updateEmailMutation.isPending}
            className="btn-primary disabled:opacity-50"
          >
            {updateEmailMutation.isPending ? 'Сохранение...' : 'Сохранить'}
          </button>
        </form>
      </div>

      {/* Password Settings */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Изменить пароль</h2>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Текущий пароль
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Новый пароль
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Подтвердите новый пароль
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input w-full"
              required
            />
          </div>
          <button
            type="submit"
            disabled={changePasswordMutation.isPending}
            className="btn-primary"
          >
            {changePasswordMutation.isPending ? 'Изменение...' : 'Изменить пароль'}
          </button>
        </form>
      </div>

      {/* Account Info */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Информация об аккаунте</h2>
        <div className="space-y-2 text-sm">
          <p>
            <span className="text-gray-500">ID:</span>{' '}
            <span className="text-gray-900">{user?.id}</span>
          </p>
          <div className="flex items-center gap-3">
            <span className="text-gray-500">Email подтверждён:</span>
            <span className={user?.email_verified ? 'text-green-600' : 'text-red-600'}>
              {user?.email_verified ? 'Да' : 'Нет'}
            </span>
            {!user?.email_verified && (
              <button
                onClick={() => resendVerificationMutation.mutate()}
                disabled={resendVerificationMutation.isPending}
                className="text-sm text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
              >
                {resendVerificationMutation.isPending ? 'Отправка...' : 'Отправить повторно'}
              </button>
            )}
          </div>
          <p>
            <span className="text-gray-500">Дата регистрации:</span>{' '}
            <span className="text-gray-900">
              {user?.created_at
                ? new Date(user.created_at).toLocaleDateString('ru-RU')
                : '—'}
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}
